import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Footer from '../components/Footer';
import './HomePage.css';

const HomePage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, guestLocation, setGuestLocation, hasLocation, openAddressModal, openSignupModal } = useAuth();
  const [loading, setLoading] = useState(true);
  const [statusText, setStatusText] = useState('Detecting your location...');

  useEffect(() => {
    if (authLoading) {
      setStatusText('Loading...');
    } else {
      setStatusText('Detecting your location...');
    }
  }, [authLoading]);

  useEffect(() => {
    // Wait for auth to finish loading before making decisions
    if (authLoading) return;

    // If user is logged in, always go to explore
    if (user) {
      navigate('/explore');
      return;
    }

    // If we already have a location (guest with saved address from localStorage), go to explore
    if (hasLocation()) {
      setLoading(false);
      navigate('/explore');
      return;
    }

    // Otherwise, show the home page immediately — do NOT auto-request GPS or show address modal.
    // The user can click "Continue as Guest" to enter their address manually.
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading, guestLocation]);

  const handleGuestContinue = () => {
    openAddressModal();
  };

  const handleSignup = () => {
    openSignupModal();
  };

  if (loading) {
    return (
      <div className="ff-home-loading">
        <div className="ff-spinner-container">
          <div className="ff-spinner"></div>
          <p className="ff-loading-text">{statusText}</p>
          <p className="ff-loading-subtext" style={{ fontSize: '0.85rem', opacity: 0.6, marginTop: 8 }}>
            This may take a few seconds
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="ff-home-page">
      {/* Hero Section */}
      <div className="ff-hero">
        <div className="ff-hero-ingredients">
          {/* Small background elements */}
          <div className="ff-ingredient tomato"></div>
          <div className="ff-ingredient leaf"></div>
          <div className="ff-ingredient garlic"></div>
          <div className="ff-ingredient lemon"></div>
          <div className="ff-ingredient strawberry"></div>
          {/* Medium elements */}
          <div className="ff-ingredient pepper"></div>
          <div className="ff-ingredient mushroom"></div>
          <div className="ff-ingredient carrot"></div>
          <div className="ff-ingredient banana"></div>
          <div className="ff-ingredient egg"></div>
          <div className="ff-ingredient sausage"></div>
          <div className="ff-ingredient cheese"></div>
          {/* Large foreground elements */}
          <div className="ff-ingredient broccoli"></div>
          <div className="ff-ingredient onion"></div>
          <div className="ff-ingredient bread"></div>
        </div>
        <div className="ff-hero-overlay">
          <div className="ff-hero-content">
            <div className="ff-logo-container">
              <img src="/fodyfee_logo.png" alt="FodyFee" className="ff-logo fody-shine" />
            </div>
            <h1 className="ff-hero-title">Discover Local Flavors</h1>
            <p className="ff-hero-subtitle">
              Find the best restaurants near you and order online for delivery or pickup.
            </p>

            <div className="ff-hero-actions">
              <button className="ff-btn ff-btn-primary" onClick={handleGuestContinue}>
                <i className="fas fa-map-marker-alt mr-2"></i>
                Continue as Guest
              </button>
              <button className="ff-btn ff-btn-outline" onClick={handleSignup}>
                <i className="fas fa-user-plus mr-2"></i>
                Create Account
              </button>
            </div>

            <div className="ff-login-link">
              Already have an account?{' '}
              <button className="ff-link" onClick={handleSignup}>
                Log In
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Fody Flight Promo Banner */}
      <div className="ff-flight-banner">
        <div className="ff-flight-banner-inner">
          <div className="ff-flight-icon-wrap">
            <img src="/fodyAI.png" alt="Fody Flight" className="ff-flight-bird fody-shine" />
            <span className="ff-flight-badge">AI</span>
          </div>
          <div className="ff-flight-content">
            <h2 className="ff-flight-title">
              Meet <span className="ff-flight-brand">Fody Flight</span>
              <span className="ff-flight-beta">BETA</span>
            </h2>
            <p className="ff-flight-desc">
              Your personal AI food assistant. Reorder your favorites in one tap, track your deliveries in real time, and get smart recommendations tailored to your taste.
            </p>
            <div className="ff-flight-actions">
              <span className="ff-flight-tag">
                <i className="fas fa-undo"></i> One-Tap Reorder
              </span>
              <span className="ff-flight-tag">
                <i className="fas fa-map-marker-alt"></i> Live Tracking
              </span>
              <span className="ff-flight-tag">
                <i className="fas fa-magic"></i> Smart Recommendations
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="ff-features">
        <div className="container">
          <div className="row">
            <div className="col-md-4">
              <div className="ff-feature-card">
                <div className="ff-feature-icon">
                  <i className="fas fa-search-location"></i>
                </div>
                <h3>Find Nearby</h3>
                <p>Discover restaurants and stores in your area, sorted by distance.</p>
              </div>
            </div>
            <div className="col-md-4">
              <div className="ff-feature-card">
                <div className="ff-feature-icon">
                  <i className="fas fa-utensils"></i>
                </div>
                <h3>Browse Menus</h3>
                <p>Explore full menus with photos, descriptions, and prices.</p>
              </div>
            </div>
            <div className="col-md-4">
              <div className="ff-feature-card">
                <div className="ff-feature-icon">
                  <i className="fas fa-motorcycle"></i>
                </div>
                <h3>Order Online</h3>
                <p>Choose delivery or pickup and enjoy your favorites.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Fodyes Rewards Banner */}
      <div className="ff-fodyes-banner">
        <div className="ff-fodyes-banner-inner">
          <div className="ff-fodyes-icon-wrap">
            <div className="ff-fodyes-coin">
              <i className="fas fa-coins"></i>
            </div>
            <span className="ff-fodyes-sparkle">✦</span>
          </div>
          <div className="ff-fodyes-content">
            <h2 className="ff-fodyes-title">
              Earn <span className="ff-fodyes-brand">Fodyes</span> on Every Order
            </h2>
            <p className="ff-fodyes-desc">
              Turn every bite into rewards. Earn Fodyes automatically when you order at participating restaurants, then redeem them as real discounts on your next purchase. The more you explore, the more you save.
            </p>
            <div className="ff-fodyes-actions">
              <span className="ff-fodyes-tag">
                <i className="fas fa-gift"></i> Earn on Every Order
              </span>
              <span className="ff-fodyes-tag">
                <i className="fas fa-tag"></i> Redeem as Discount
              </span>
              <span className="ff-fodyes-tag">
                <i className="fas fa-store"></i> Participating Restaurants
              </span>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default HomePage;
